import { FILE_EXTENSIONS, MAX_FILE_BYTES, ORDER_COLORS, isOrderColor, fileExtension } from "../shared/order-domain";
import { orderWriteSchema } from "../shared/order-schema";
import { discardFile, uploadFile, write, type VisibleOrder } from "./api";
import { element, escapeHtml as html, formError, showToast } from "./ui";

interface Draft {
  localId: number;
  id?: number;
  file_key: string;
  original_key: string;
  file_name: string;
  color: string;
  quantity: string;
  file?: File;
  state: "uploading" | "done" | "error";
  error?: string;
  task?: Promise<void>;
  cancelled: boolean;
}

export function initializeOrderForm(onSaved: (order: VisibleOrder) => void): { open(order?: VisibleOrder): void } {
  const dialog = element<HTMLDialogElement>("orderDialog");
  const form = element<HTMLFormElement>("orderForm");
  const container = element("fileDrafts");
  const fileInput = element<HTMLInputElement>("fileInput");
  const saveButton = element<HTMLButtonElement>("saveOrderButton");
  let drafts: Draft[] = [];
  let editingId: number | null = null;
  let sequence = 0;
  let saving = false;
  fileInput.accept = FILE_EXTENSIONS.map((extension) => `.${extension}`).join(",");
  element("uploadHelp").textContent = `${FILE_EXTENSIONS.map((extension) => extension.toUpperCase()).join(" / ")} · 单个文件最多 ${MAX_FILE_BYTES / 1024 / 1024} MB`;

  function cleanup(draft: Draft): void {
    draft.cancelled = true;
    if (draft.file_key && draft.file_key !== draft.original_key) {
      void discardFile(draft.file_key).catch((error) => console.error("upload cleanup failed", error));
    }
  }

  function render(): void {
    container.innerHTML = drafts.length ? drafts.map((draft) => `<div class="file-draft" data-draft="${draft.localId}"><div class="draft-name"><strong title="${html(draft.file_name)}">${html(draft.file_name)}</strong><span class="upload-state ${draft.state}">${draft.state === "done" ? "✓ 已上传" : draft.state === "uploading" ? "正在上传…" : html(draft.error || "上传失败")}</span></div><label class="field"><span>颜色 / 材料</span><select data-field="color" required>${isOrderColor(draft.color) ? "" : `<option value="${html(draft.color)}" selected>${html(draft.color)}（原材料）</option>`}${ORDER_COLORS.map((color) => `<option value="${html(color)}" ${color === draft.color ? "selected" : ""}>${html(color)}</option>`).join("")}</select></label><label class="field quantity-field"><span>数量</span><input data-field="quantity" type="number" min="1" max="999" step="1" value="${html(draft.quantity)}" required /></label><div class="draft-actions">${draft.state === "error" ? '<button type="button" class="text-button" data-retry>重试</button>' : ""}<button type="button" class="close-button" data-remove aria-label="移除 ${html(draft.file_name)}">×</button></div></div>`).join("") : '<p class="upload-empty">添加一个或多个文件，每个文件可单独设置颜色和数量。</p>';
  }

  function startUpload(draft: Draft): void {
    if (!draft.file) return;
    draft.state = "uploading";
    draft.error = undefined;
    draft.task = uploadFile(draft.file).then(async (result) => {
      draft.file_key = result.key;
      draft.state = "done";
      if (draft.cancelled) await discardFile(result.key);
    }).catch((error: unknown) => {
      draft.state = "error";
      draft.error = error instanceof Error ? error.message : "上传失败";
    }).finally(() => { if (!draft.cancelled && !saving) render(); });
  }

  fileInput.addEventListener("change", () => {
    formError("orderError", null);
    for (const file of fileInput.files ?? []) {
      if (drafts.length >= 100) { formError("orderError", "每单最多添加 100 个文件"); break; }
      if (!fileExtension(file.name) || !file.size || file.size > MAX_FILE_BYTES || file.name.length > 255) {
        formError("orderError", `${file.name}：格式不支持或文件大小无效`);
        continue;
      }
      const draft: Draft = { localId: ++sequence, file_key: "", original_key: "", file_name: file.name,
        color: ORDER_COLORS[0], quantity: "1", file, state: "uploading", cancelled: false };
      drafts.push(draft);
      startUpload(draft);
    }
    fileInput.value = "";
    render();
  });
  container.addEventListener("input", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLSelectElement)) return;
    const host = input.closest<HTMLElement>("[data-draft]");
    const draft = drafts.find((candidate) => String(candidate.localId) === host?.dataset.draft);
    if (draft && (input.dataset.field === "color" || input.dataset.field === "quantity")) draft[input.dataset.field] = input.value;
  });
  container.addEventListener("click", (event) => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("button") : null;
    const host = button?.closest<HTMLElement>("[data-draft]");
    const draft = drafts.find((candidate) => String(candidate.localId) === host?.dataset.draft);
    if (!draft || !button) return;
    if (button.hasAttribute("data-remove")) {
      cleanup(draft);
      drafts = drafts.filter((candidate) => candidate !== draft);
    } else if (button.hasAttribute("data-retry")) startUpload(draft);
    render();
  });
  dialog.addEventListener("cancel", (event) => { if (saving) event.preventDefault(); });
  dialog.addEventListener("close", () => {
    for (const draft of drafts) cleanup(draft);
    drafts = [];
    form.reset();
  });
  dialog.querySelectorAll<HTMLButtonElement>("[data-close]").forEach((button) => button.addEventListener("click", () => { if (!saving) dialog.close(); }));
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (saving) return;
    formError("orderError", null);
    saving = true;
    form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>("input,select,textarea,button").forEach((input) => { input.disabled = true; });
    saveButton.textContent = "正在保存…";
    try {
      await Promise.all(drafts.map((draft) => draft.task));
      if (!drafts.length) throw new Error("请至少添加一个打印文件");
      if (drafts.some((draft) => draft.state !== "done")) throw new Error("有文件上传失败，请点击重试后再保存");
      const input = {
        order_time: element<HTMLInputElement>("orderTime").value,
        xianyu_id: element<HTMLInputElement>("buyerId").value,
        address: element<HTMLTextAreaElement>("address").value,
        total_price: element<HTMLInputElement>("totalPrice").value,
        notes: element<HTMLInputElement>("notes").value,
        items: drafts.map((draft) => ({ ...(draft.id === undefined ? {} : { id: draft.id }),
          file_key: draft.file_key, file_name: draft.file_name, color: draft.color, quantity: draft.quantity })),
      };
      const parsed = orderWriteSchema.safeParse(input);
      if (!parsed.success) throw new Error(parsed.error.issues[0].message);
      const order = await write<VisibleOrder>(editingId === null ? "/orders" : `/orders/${editingId}`, editingId === null ? "POST" : "PUT", input);
      drafts = [];
      dialog.close();
      onSaved(order);
      showToast(editingId === null ? "订单已添加，请在微信通知代打方" : "订单已保存");
    } catch (error) {
      formError("orderError", error);
    } finally {
      saving = false;
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement>("input,select,textarea,button").forEach((input) => { input.disabled = false; });
      saveButton.textContent = "保存订单";
      render();
    }
  });
  return {
    open(order) {
      form.reset();
      formError("orderError", null);
      editingId = order?.id ?? null;
      element("orderDialogTitle").textContent = order ? "编辑订单" : "添加订单";
      const now = new Date();
      const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
      element<HTMLInputElement>("orderTime").value = order?.order_time.slice(0, 16) ?? local;
      element<HTMLInputElement>("buyerId").value = order?.xianyu_id ?? "";
      element<HTMLTextAreaElement>("address").value = order?.address ?? "";
      element<HTMLInputElement>("totalPrice").value = order && "total_price_cents" in order && order.total_price_cents !== null ? (order.total_price_cents / 100).toFixed(2) : "";
      element<HTMLInputElement>("notes").value = order?.notes ?? "";
      drafts = order?.items.map((item) => ({ localId: ++sequence, id: item.id, file_key: item.file_key,
        original_key: item.file_key, file_name: item.file_name, color: item.color,
        quantity: String(item.quantity), state: "done", cancelled: false })) ?? [];
      render();
      dialog.showModal();
    },
  };
}
